import type { ReactNode } from 'react';

import { ProjectCategory } from '../../../../../shared/projectsV2/common';
import { ProjectSortPrefixes } from '../../../../../shared/projectsV2/sorting';
import QualityRequired from '../../../generic/Gates/QualityRequired';
import SecurityRequired from '../../../generic/Gates/SecurityRequired';
import { icons } from '../../../generic/Icon';

import type { SortingContext, SortingDropdownConfig } from './SortingDropdown';

// Issues dropdown configuration
export const createIssuesDropdownConfig = (): SortingDropdownConfig => {
  const categories = [
    {
      key: 'issues-total',
      label: 'Total issues',
      icon: icons.globeOutline,
      prefix: ProjectSortPrefixes.Total,
      // Always visible - both Projects & Release Groups
    },
    {
      key: 'issues-licensing',
      label: 'Licensing issues',
      icon: icons.fileOutline,
      prefix: ProjectSortPrefixes.Licensing,
      isVisible: (context: SortingContext) => context.category === ProjectCategory.Projects,
    },
    {
      key: 'issues-security',
      label: 'Security issues',
      icon: icons.shieldHalved,
      prefix: ProjectSortPrefixes.Security,
      isVisible: (context: SortingContext) => context.category === ProjectCategory.Projects,
      wrapper: (children: ReactNode) => <SecurityRequired>{children}</SecurityRequired>,
    },
    {
      key: 'issues-quality',
      label: 'Quality issues',
      icon: icons.sparkle,
      prefix: ProjectSortPrefixes.Quality,
      isVisible: (context: SortingContext) => context.category === ProjectCategory.Projects,
      wrapper: (children: ReactNode) => <QualityRequired>{children}</QualityRequired>,
    },
  ];

  return {
    categories,
    defaultCategoryKey: 'issues-total',
    menuId: 'projects-table-issues-sorting-dropdown',
    getConfig: () => ({ categories }),
  };
};

// Revision dropdown configuration
export const createRevisionDropdownConfig = (): SortingDropdownConfig => {
  const categories = [
    {
      key: 'latest-scan',
      label: 'Last policy scan',
      prefix: ProjectSortPrefixes.LatestScan,
      // Always visible - both Projects & Release Groups
    },
    {
      key: 'last-analyzed',
      label: 'Revision analyzed',
      prefix: ProjectSortPrefixes.LastAnalyzed,
      isVisible: (context: SortingContext) => context.category === ProjectCategory.Projects,
    },
  ];

  return {
    categories,
    defaultCategoryKey: 'latest-scan',
    menuId: 'projects-table-revision-sorting-dropdown',
    getConfig: () => ({ categories }),
  };
};

// Title dropdown configuration (future-proofed with isVisible pattern)
export const createTitleDropdownConfig = (): SortingDropdownConfig => {
  const categories = [
    {
      key: 'title',
      label: (context: SortingContext) => {
        const labels = {
          projects: 'Title',
          releaseGroups: 'Title',
          sharedProjects: 'Shared Projects',
        };
        return labels[context.category];
      },
      prefix: ProjectSortPrefixes.Title,
      // Currently visible for Projects & Release Groups, but ready for future constraints if needed
      isVisible: () => true,
    },
  ];

  return {
    categories,
    defaultCategoryKey: 'title',
    menuId: 'projects-table-title-sorting-dropdown',
    getConfig: () => ({ categories }),
  };
};
